import BackButton from "@/components/BackButton"
import BadgeIcon from "@/components/BadgeIcon"
import { Button } from "@/components/chadcn/Button"
import { Label } from "@/components/chadcn/Label"
import CopyRightsFooter from "@/components/CopyRightsFooter"
import { EmailInput } from "@/components/InputFields"
import Page from "@/components/Page"
import Spinner from "@/components/Spinner"
import { useResendInvitationMutation } from "@/data/queries"
import { ROUTES } from "@/routes"
import { requestInvitationSchema, type RequestInvitationFormData } from "@/schemas/auth"
import { zodResolver } from "@hookform/resolvers/zod"
import { useState } from "react"
import { useForm } from "react-hook-form"
import { useNavigate } from "react-router"
import { toast } from "sonner"

const RequestInvitationPage = () => {
  const navigate = useNavigate()
  const [hasSubmitted, setHasSubmitted] = useState(false)
  const { mutateAsync: resendInvitation, isPending } = useResendInvitationMutation()

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<RequestInvitationFormData>({
    mode: "all",
    defaultValues: { email: "" },
    resolver: zodResolver(requestInvitationSchema),
  })

  const goToSignIn = () => navigate(ROUTES.SIGN_IN, { replace: true })

  const onSubmit = async (formData: RequestInvitationFormData) => {
    try {
      await resendInvitation({ email: formData.email })
      setHasSubmitted(true)
    } catch {
      toast.error("We couldn't send your invitation request. Please try again.")
    }
  }

  if (hasSubmitted) {
    return (
      <Page showAppbar={false}>
        <div className="flex min-h-page flex-col gap-6">
          <div className="flex flex-1 flex-col items-center justify-center gap-4 text-center">
            <BadgeIcon variant="success" />
            <h1 className="text-h2 font-bold">Check your email</h1>
            <p className="text-base text-primary-contrast">
              If that email address matches an account in our system, we&apos;ve sent a new
              invitation link to it. It can take a few minutes to arrive.
            </p>
          </div>
          <Button type="button" className="w-full py-2 text-base" onClick={goToSignIn}>
            Back to Login
          </Button>
          <CopyRightsFooter />
        </div>
      </Page>
    )
  }

  return (
    <Page showAppbar={false}>
      <div className="flex min-h-page flex-col gap-6">
        <div className="flex flex-1 flex-col gap-6">
          <BackButton customClickHandler={goToSignIn} />
          <div className="flex flex-col gap-3">
            <h1 className="text-h2 font-bold">Request a new invitation</h1>
            <p className="text-base text-primary-contrast">
              Enter the email address on file with your clinic. If it matches an account, we&apos;ll
              email you a new invitation link.
            </p>
          </div>
          <form
            id="request-invitation-form"
            className="flex flex-col gap-1"
            onSubmit={handleSubmit(onSubmit)}
          >
            <Label htmlFor="email">Email address</Label>
            <EmailInput
              id="email"
              autoComplete="email"
              disabled={isPending}
              {...register("email")}
              placeholder="email@example.com"
              isError={!!errors.email?.message}
            />
            {errors.email?.message && <p className="text-sm text-error">{errors.email.message}</p>}
          </form>
        </div>
        <Button
          type="submit"
          form="request-invitation-form"
          disabled={isPending}
          className="flex w-full gap-3 py-2 text-base"
        >
          {isPending && <Spinner />}
          Send invitation
        </Button>
        <CopyRightsFooter />
      </div>
    </Page>
  )
}

export default RequestInvitationPage
